// Tiny in-MFE i18n shim. The shared @sms/i18n package owns the locale
// bundles + parity-test gate; this file picks one bundle and exposes a
// dot-path `t(key, vars?)` so components don't have to thread the
// bundle through props.
//
// When the shell wires react-i18next, this shim retires; the keys we
// use here already exist in @sms/i18n so the migration is mechanical.

import { resources, type Locale } from "@sms/i18n";

type Bundle = (typeof resources)["en"]["common"];

function lookup(bundle: Bundle, path: string): string | undefined {
  const parts = path.split(".");
  let cur: unknown = bundle;
  for (const part of parts) {
    if (typeof cur !== "object" || cur === null) return undefined;
    cur = (cur as Record<string, unknown>)[part];
  }
  return typeof cur === "string" ? cur : undefined;
}

function interpolate(template: string, vars: Record<string, string>): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, name) => vars[name] ?? `{{${name}}}`);
}

/**
 * Translator scoped to one locale. Missing keys fall back to the key
 * itself (with curly braces stripped) — visually wrong but never an
 * exception, which matches how react-i18next behaves with
 * `returnNull: false`.
 */
export function makeT(locale: Locale): (key: string, vars?: Record<string, string>) => string {
  const bundle = resources[locale].common;
  return (key, vars) => {
    const raw = lookup(bundle, key);
    if (raw === undefined) return key;
    return vars ? interpolate(raw, vars) : raw;
  };
}
